import { useState, useEffect } from "react";
import { Box, Button, Container, Typography, Grid } from "@mui/material";
import PasswordInput from "../PasswordInput/PasswordInput";
import { CredentialMgmtDataService } from "../../services/CredentialMgmtDataService";
import { _invalidTokenMessage, _unknownError, _validationError } from "../../helpers";
import "./MsCredMgmtSetPassword.css";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useFormik } from "formik";
import { useConfig } from "../../contexts/ConfigContext";
import StatusScreenMs from "../StatusScreenMs/StatusScreenMs";
import MsShimmer from "../MsShimmer/MsShimmer";
import { MsActivationLinkExpired } from "../MsActivationLinkExpired";

const MsCredMgmtSetPassword = () => {
  const { search } = useLocation();
  const { config } = useConfig();
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [validStatus, setValidStatus] = useState(null);
  const navigate = useNavigate();
  const { tenant } = useParams();
  const credentialService = CredentialMgmtDataService();

  const token = search?.split("=")[1];
  const passwordChangedUrl = `/ciam/v1/${tenant}/accounts/password-changed`;
  useEffect(() => {
    if (token) {
      credentialService
        .validateToken("/accounts/operations/pre-validate", { token })
        .then((response) => {
          setValidStatus({
            status: response.status,
            step: "pre-validation",
            message: "Success",
          });
        })
        .catch((error) => {
          const status = error.response?.status;
          setValidStatus({
            status,
            step: "pre-validation",
            message: status === 400 ? "Invalid link" : status === 500 ? _unknownError : "Something went wrong",
          });
        });
    } else {
      setValidStatus({
        status: 400,
        step: "pre-validation",
        message: "Invalid link",
      });
    }
  }, [token]);

  const getPasswordRequirements = (password) => ({
    minLength: password.length >= 10,
    specialChar: /[^A-Za-z0-9]/.test(password),
    uppercase: /[A-Z]/.test(password),
    lowercase: /[a-z]/.test(password),
    number: /[0-9]/.test(password),
  });

  const formik = useFormik({
    initialValues: { password: "", confirmedPassword: "" },
    validate: (values) => {
      const errors = {};
      if (values.password !== values.confirmedPassword) {
        errors.confirmedPassword = "Passwords do not match";
      }

      const reqs = getPasswordRequirements(values.password);
      const allValid = Object.values(reqs).every(Boolean);
      setIsFormValid(allValid && values.password === values.confirmedPassword);

      return errors;
    },
    onSubmit: (values) => {
      setIsLoading(true);
      credentialService
        .activateUser("/accounts/operations/change-password", {
          ...values,
          token,
        })
        .then(() => {
          setIsLoading(false);
          navigate(passwordChangedUrl);
        })
        .catch((error) => {
          setIsLoading(false);
          const status = error?.response?.status;

          if (status >= 400 && status < 500) {
            const errorMessages = error.response.data?.error_validations?.map((obj) => obj.message) ?? [];

            if (errorMessages.some((msg) => msg.toLowerCase() === "invalid token")) {
              setValidStatus({
                status,
                step: "activation",
                message: _invalidTokenMessage,
              });
            } else if (errorMessages.length) {
              setValidStatus({
                status,
                step: "activation",
                message: _validationError,
                validationErrors: errorMessages,
              });
            } else {
              setValidStatus({ status, step: "activation", message: _unknownError });
            }

            // setShowErrorBanner(true);
          } else if (status === 500) {
            setValidStatus({ status, message: _unknownError });
          }
        });
    },
  });

  if (!config || isLoading) return <MsShimmer />;
  if (!validStatus) return <MsShimmer />;
  if (validStatus.status === 400 && !validStatus.validationErrors) return <MsActivationLinkExpired />;
  if (validStatus.status === 500) return <StatusScreenMs status={validStatus} />;

  const requirements = getPasswordRequirements(formik.values.password);

  const renderRequirement = (text, condition) => (
    <span className={`validation-messages ${condition ? "req-met" : "req-not-met"}`}>
      {condition ? (
        <img src="/ciam/v1/static/images/ms/check_circle_outline.svg" alt="checkmark" />
      ) : (
        <img src="/ciam/v1/static/images/ms/highlight_off.svg" alt="cross" />
      )}
      {text}
    </span>
  );

  return (
    <Grid className="ms-set-password-container">
      <div className="ms-wl-bank-logo" data-testid="svg-icon">
        <img src="/ciam/v1/static/images/ms/Worldline-Mint-Horizontal.svg" alt="Worldline" />
      </div>
      <Grid item xs={7} className="right-gradient" data-testid="right-gradient-circle">
        <img src="/ciam/v1/static/images/ms/right-circle.svg" alt="right-circular-gradient" />
      </Grid>
      <div className="ms-set-password-left">
        <Container className="ms-form-container">
          <Box>
            <Typography variant="h5" fontWeight="600" gutterBottom className="ms-set-password-title">
              Set a new password
            </Typography>
            <Typography variant="body1" gutterBottom>
              Choose a new password
            </Typography>

            <form onSubmit={formik.handleSubmit} noValidate>
              <PasswordInput
                label="New password"
                name="password"
                className="password-input-ms"
                value={formik.values.password}
                onChange={formik.handleChange}
                showPassword={showNewPassword}
                onBlur={formik.handleBlur}
                toggleShowPassword={() => setShowNewPassword(!showNewPassword)}
                error={formik.touched.password && formik.errors.password}
                isAutoFocus={true}
              />

              <div className="req-met-wrapper">
                {renderRequirement("min 10 characters", requirements.minLength)}
                {renderRequirement("1 special character", requirements.specialChar)}
                {renderRequirement("1 uppercase letter", requirements.uppercase)}
                {renderRequirement("1 lowercase letter", requirements.lowercase)}
                {renderRequirement("1 number", requirements.number)}
              </div>

              <PasswordInput
                label="Confirm new password"
                name="confirmedPassword"
                className="password-input-ms"
                value={formik.values.confirmedPassword}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                showPassword={showConfirmPassword}
                toggleShowPassword={() => setShowConfirmPassword(!showConfirmPassword)}
                error={formik.touched.confirmedPassword && formik.errors.confirmedPassword}
                isAutoFocus={false}
              />

              <Button
                variant="contained"
                color="primary"
                fullWidth
                type="submit"
                sx={{ mt: 3 }}
                disabled={!isFormValid || isLoading}
              >
                Save new password
              </Button>
            </form>
          </Box>
        </Container>
      </div>
    </Grid>
  );
};

export default MsCredMgmtSetPassword;
